import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Document, pdfjs } from 'react-pdf';
import LazyPDFPage from './lazy-pdf-page/LazyPDFPage';
import { SelectionState } from './lazy-pdf-page/LazyPDFPage.props';
import { PDFCitationPayload, Rect } from './pdf-utils/constants';
import { mergeRects } from './pdf-utils/rect-utils';
import { PDFViewerProps, PDFViewerViewMode } from './PDFViewer.props';
import {
  PdfPageViewerLoader,
  PdfViewerContentWrapper,
  PdfViewerStateContainer,
  PdfViewerWrapper,
  StyledCitationNavButton,
  StyledCitationNavigatorWrapper,
  StyledHighlightableTextNotification,
  THUMBNAIL_WIDTH,
} from './PDFViewer.styles';
import { Logger } from '../../../utilities';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const PdfLoader = () => <PdfPageViewerLoader>Loading PDF...</PdfPageViewerLoader>;

const PDFViewer = ({
  src,
  base64Content,
  allowPageSelection = false,
  selectedPages = new Set(),
  onPageToggle,
  viewMode = PDFViewerViewMode.DOCUMENT,
  jsonData,
  activeCitation,
  onCitationPushed,
  searchHighlight,
}: PDFViewerProps) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pdfData, setPdfData] = useState<string | ArrayBuffer | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selection, setSelection] = useState<SelectionState | null>(null);
  const [activeTokenRefIndex, setActiveTokenRefIndex] = useState(0);
  const [forcedVisiblePage, setForcedVisiblePage] = useState<number | null>(null);
  const [showCitationNotification, setShowCitationNotification] = useState(false);
  const [viewerHostElement, setViewerHostElement] = useState<HTMLDivElement | null>(null);

  const pdfOwnerDocument = viewerHostElement?.ownerDocument as HTMLDocument | undefined;
  const documentOptions = useMemo(() => {
    if (!pdfOwnerDocument) {
      return undefined;
    }

    return { ownerDocument: pdfOwnerDocument };
  }, [pdfOwnerDocument]);

  const totalTokenRefs = activeCitation?.tokenReferences.length ?? 0;

  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  useEffect(() => {
    if (!viewerHostElement) {
      return;
    }

    const measure = () => setContainerWidth(viewerHostElement.clientWidth);
    measure();

    const observer = new ResizeObserver(() => measure());
    observer.observe(viewerHostElement);
    return () => observer.disconnect();
  }, [viewerHostElement]);

  const calculatedWidth = useMemo(() => {
    if (viewMode === PDFViewerViewMode.THUMBNAIL) {
      return THUMBNAIL_WIDTH;
    }

    const MAX_WIDTH = 1200;
    const VIEWER_BUFFER = 40;
    const CONTAINER_BUFFER = 100;

    const available =
      containerWidth ?? (typeof window !== 'undefined' ? window.innerWidth - CONTAINER_BUFFER : MAX_WIDTH);
    return Math.min(MAX_WIDTH, available - VIEWER_BUFFER);
  }, [viewMode, containerWidth]);

  useEffect(() => {
    const processPdfData = async () => {
      setLoading(true);
      setError(null);
      try {
        if (base64Content) {
          const binaryString = atob(base64Content);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          setPdfData(bytes.buffer);
        } else if (src) {
          setPdfData(src);
        } else {
          setError('No PDF source provided');
        }
      } catch (err) {
        Logger.error('Error processing PDF data:', err);
        setError('Failed to process PDF data');
      } finally {
        setLoading(false);
      }
    };
    processPdfData();
  }, [base64Content, src]);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  }, []);

  const onDocumentLoadError = useCallback((error: Error) => {
    Logger.error('Error loading PDF:', error);
    setError('Failed to load PDF document');
  }, []);

  // Reset index and force-render the target page when the active citation changes
  useEffect(() => {
    setActiveTokenRefIndex(0);
  }, [activeCitation?.id]);

  useEffect(() => {
    if (!activeCitation) {
      setForcedVisiblePage(null);
      return;
    }
    const tokenRef = activeCitation.tokenReferences[activeTokenRefIndex];
    if (!tokenRef) {
      return;
    }

    // External API may return page under different field names depending on citation source
    let pageNumber: number | null = tokenRef.page ?? (tokenRef as any).page_number ?? (tokenRef as any).pageNumber ?? null;

    // If page is still unknown but we have jsonData, find the page that contains these token_ids
    if (pageNumber === null && jsonData && tokenRef.token_ids?.length > 0) {
      const tokenIdSet = new Set(tokenRef.token_ids);
      const matchingPage = jsonData.find(pd => pd.words.some(w => tokenIdSet.has(w.id)));
      if (matchingPage) {
        pageNumber = matchingPage.page;
      }
    }

    setForcedVisiblePage(pageNumber);

    if (pageNumber === null) {
      return;
    }

    // If jsonData is available, wait for the citation highlight element and scroll to it
    if (jsonData) {
      const existing = document.getElementById('active-citation-highlight');
      if (existing) {
        existing.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      let safetyTimer: ReturnType<typeof setTimeout>;
      const observer = new MutationObserver(() => {
        const highlightEl = document.getElementById('active-citation-highlight');
        if (highlightEl) {
          observer.disconnect();
          clearTimeout(safetyTimer);
          highlightEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      safetyTimer = setTimeout(() => observer.disconnect(), 3000);

      return () => {
        observer.disconnect();
        clearTimeout(safetyTimer);
      };
    }

    // No jsonData — scroll to the page element
    const pageEl = document.getElementById(`pdf-page-${pageNumber}`) as HTMLElement | null;
    if (pageEl) {
      pageEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // Page element not yet in DOM — observe until it appears
    let safetyTimer: ReturnType<typeof setTimeout>;
    const observer = new MutationObserver(() => {
      const el = document.getElementById(`pdf-page-${pageNumber}`);
      if (el) {
        observer.disconnect();
        clearTimeout(safetyTimer);
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    safetyTimer = setTimeout(() => observer.disconnect(), 3000);
    return () => {
      observer.disconnect();
      clearTimeout(safetyTimer);
    };
  }, [activeCitation, activeTokenRefIndex, jsonData]);

  // Scroll to search highlight within the PDF scroll container only.
  // Two-phase: first bring the page container into view so the lazy page
  // renders, then once the highlight element appears scroll precisely to it.
  useEffect(() => {
    if (!searchHighlight) {
      return;
    }

    setForcedVisiblePage(searchHighlight.pageNumber);

    const scrollContainer = viewerHostElement?.closest('#pdf-viewer-scroll-container') ?? viewerHostElement?.parentElement;
    if (!scrollContainer) {
      return;
    }

    const scrollToElement = (el: Element) => {
      const containerRect = scrollContainer.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const targetTop = scrollContainer.scrollTop + (elRect.top - containerRect.top) - containerRect.height / 2;
      scrollContainer.scrollTo({ top: targetTop, behavior: 'smooth' });
    };

    // Phase 1: scroll the page container into view so forceVisible + intersection triggers rendering
    const pageEl = document.getElementById(`pdf-page-${searchHighlight.pageNumber}`);
    if (pageEl) {
      scrollToElement(pageEl);
    }

    // Phase 2: wait for the highlight element to appear after the page renders, then scroll precisely
    const scrollToHighlight = () => {
      const el = document.getElementById('search-highlight');
      if (!el) {
        return false;
      }
      // Let the page finish layout before the final scroll
      requestAnimationFrame(() => scrollToElement(el));
      return true;
    };

    if (scrollToHighlight()) {
      return;
    }

    let safetyTimer: ReturnType<typeof setTimeout>;
    const observer = new MutationObserver(() => {
      if (scrollToHighlight()) {
        observer.disconnect();
        clearTimeout(safetyTimer);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    safetyTimer = setTimeout(() => observer.disconnect(), 5000);
    return () => {
      observer.disconnect();
      clearTimeout(safetyTimer);
    };
  }, [searchHighlight, viewerHostElement]);

  const handlePrevTokenRef = useCallback(() => {
    setActiveTokenRefIndex(i => Math.max(0, i - 1));
  }, []);

  const handleNextTokenRef = useCallback(() => {
    setActiveTokenRefIndex(i => Math.min((activeCitation?.tokenReferences.length ?? 1) - 1, i + 1));
  }, [activeCitation?.tokenReferences.length]);

  const handleMouseUp = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!jsonData || viewMode !== PDFViewerViewMode.DOCUMENT) {
        return;
      }

      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
        setSelection(null);
        return;
      }

      const range = sel.getRangeAt(0);
      const startNode = range.startContainer.parentElement;
      const pageContainer = startNode?.closest('.custom-text-layer') as HTMLElement | null;

      if (!pageContainer) {
        setSelection(null);
        return;
      }

      const pageIndex = parseInt(pageContainer.getAttribute('data-page-index') || '0', 10);
      const pageData = jsonData[pageIndex];
      if (!pageData) {
        return;
      }

      const selectedRects: Rect[] = [];
      const selectedWordIds: number[] = [];
      let selectedText = '';

      const wordById = new Map(pageData.words.map(w => [w.id, w]));
      const spans = pageContainer.querySelectorAll<HTMLSpanElement>('span[data-word-id]');
      spans.forEach(span => {
        if (sel.containsNode(span, true)) {
          const wordId = parseInt(span.dataset.wordId!, 10);
          const word = wordById.get(wordId);
          if (!word) {
            return;
          }
          selectedRects.push({ left: word.left, top: word.top, width: word.width, height: word.height });
          selectedWordIds.push(word.id);
          selectedText += `${word.text} `;
        }
      });

      if (selectedRects.length > 0) {
        setSelection({
          pageIndex,
          page: pageData.page,
          rects: selectedRects,
          wordIds: selectedWordIds,
          text: selectedText.trim(),
        });
      } else {
        setSelection(null);
      }
    },
    [jsonData, viewMode]
  );

  const handleCite = useCallback(() => {
    if (!selection || !onCitationPushed) {
      return;
    }
    const mergedRects = mergeRects(selection.rects);
    const payload: PDFCitationPayload = {
      selectedText: selection.text,
      tokenIds: selection.wordIds,
      page: selection.page,
      rects: mergedRects,
    };
    onCitationPushed(payload);
    setSelection(null);
    setShowCitationNotification(true);
    window.getSelection()?.removeAllRanges();
  }, [selection, onCitationPushed]);

  // Auto-hide citation notification after 5 seconds
  useEffect(() => {
    if (showCitationNotification) {
      const timer = setTimeout(() => {
        setShowCitationNotification(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showCitationNotification]);

  const citationNotification = showCitationNotification
    ? createPortal(
      <StyledHighlightableTextNotification onClick={() => setShowCitationNotification(false)}>
        <span>✓</span>
        <span>Citation toggled. Now click a cell to link it.</span>
      </StyledHighlightableTextNotification>,
      document.body
    )
    : null;

  if (loading) {
    return (
      <PdfViewerWrapper>
        <PdfLoader />
      </PdfViewerWrapper>
    );
  }

  if (error) {
    return <PdfViewerStateContainer style={{ color: 'red' }}>Error: {error}</PdfViewerStateContainer>;
  }

  if (!pdfData) {
    return <PdfViewerStateContainer>No PDF available</PdfViewerStateContainer>;
  }

  const pageNumbers = Array.from({ length: numPages }, (_, i) => i + 1);

  return (
    <PdfViewerWrapper ref={setViewerHostElement} $viewMode={viewMode}>
      {documentOptions ? (
        <Document
          file={pdfData}
          options={documentOptions}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={<></>}>
          <PdfViewerContentWrapper $viewMode={viewMode}>
            {pageNumbers.map((pageNum, index) => {
              const pageData = jsonData?.find(p => p.page === pageNum);
              return (
                <LazyPDFPage
                  key={pageNum}
                  pageNumber={pageNum}
                  pageIndex={index}
                  width={calculatedWidth}
                  totalPages={numPages}
                  allowPageSelection={allowPageSelection}
                  isSelected={selectedPages.has(pageNum)}
                  onToggle={onPageToggle}
                  viewMode={viewMode}
                  pageData={pageData}
                  activeCitation={activeCitation}
                  activeTokenRefIndex={activeTokenRefIndex}
                  forceVisible={pageNum === forcedVisiblePage || pageNum === searchHighlight?.pageNumber}
                  onMouseUp={handleMouseUp}
                  selection={selection}
                  onCite={handleCite}
                  citationEnabled={!!onCitationPushed}
                  searchHighlight={searchHighlight}
                />
              );
            })}
          </PdfViewerContentWrapper>
        </Document>
      ) : (
        <PdfLoader />
      )}

      {totalTokenRefs > 1 &&
        createPortal(
          <StyledCitationNavigatorWrapper>
            <StyledCitationNavButton
              onClick={handlePrevTokenRef}
              disabled={activeTokenRefIndex === 0}
              size="small"
              variant="outlined"
              title="Previous citation">
              ↑
            </StyledCitationNavButton>
            <span>
              {activeTokenRefIndex + 1} / {totalTokenRefs}
            </span>
            <StyledCitationNavButton
              onClick={handleNextTokenRef}
              disabled={activeTokenRefIndex === totalTokenRefs - 1}
              size="small"
              variant="outlined"
              title="Next citation">
              ↓
            </StyledCitationNavButton>
          </StyledCitationNavigatorWrapper>,
          document.body
        )}
      {citationNotification}
    </PdfViewerWrapper>
  );
};

export default PDFViewer;
